import {Feature} from "ol"
import {LineString, Point} from "ol/geom"
import {Coordinate} from "ol/coordinate"
import {offset} from 'ol/sphere'
import {Fill, Icon, Stroke, Style, Circle, Text} from "ol/style"
import {t} from "@ui/common/intl"
import {replaceDigitsExceptFirst} from "@ui/common/utils"
import {animateLineStringFeature} from "@ui/services/common"
import {positionLayer} from "@ui/services/common/layers"
import mapService from "@ui/services/MapService"

type TCircle = {
  radius: number
  legend: string
}

export interface IPositionService {
  setCurrent(point: Coordinate, rotation?: number, speed?: number | null): void
}

class PositionService implements IPositionService {
  #current?: Coordinate
  #position: Feature<LineString>
  #circlesCalculations: {[resolution: number]: TCircle[]} = []
  #speed: number = 0
  #pathTime: number = 180 // время в секундах для которого показываем вектор перед судном

  constructor() {
    this.#position = new Feature<LineString>()
    animateLineStringFeature(positionLayer, this.#position, this.#boardStyle.bind(this))
    positionLayer.getSource()?.addFeature(this.#position)
  }

  setCurrent(point: Coordinate, rotation: number, speed?: number | null) {
    const lineString = new LineString(this.#current ? [this.#current, point] : [point])
    lineString.set("rotation", rotation || 0)
    this.#position.setGeometry(lineString)
    this.#current = point
    this.#speed = speed || 0
  }

  #calculateCircles(): TCircle[] {
    const resolution = Math.round((mapService.getResolution() || 0) * 10) / 10
    if (this.#circlesCalculations[resolution]) return this.#circlesCalculations[resolution]

    const {meters, resolution: azimuthResolution} = mapService.getAzimuthResolution()
    const radius = replaceDigitsExceptFirst(Math.floor(meters / 6))

    const data: TCircle[] = []
    for (let i = 1; i < 4; i++) {
      const iradius = radius * i
      const legend = iradius >= 1000 ? (iradius / 1000) + t("position.circle.unit.kilometers") : iradius + t("position.circle.unit.meters")
      data.push({radius: Math.floor(iradius / azimuthResolution), legend})
    }
    this.#circlesCalculations[resolution] = data
    return data
  }

  #boardStyle (
    point: Coordinate,
    rotation?: number
  ) {
    const circles = this.#calculateCircles()
    const [width, height] = mapService.getMapSize() || []
    const legendRotation = width > height ? Math.PI/4 : 0
    const vectorPoint = offset(point, this.#speed * this.#pathTime, rotation || 0)
    return [
      new Style({
        geometry: new Point(point),
        image: new Icon({
          src: "/board.svg",
          rotation,
          rotateWithView: true
        }),
        zIndex: 10
      }),
      new Style({
        geometry: new LineString([point, vectorPoint]),
        stroke: new Stroke({
          color: 'black',
          width: 1
        })
      }),
      new Style({
        geometry: new Point(vectorPoint),
        image: new Icon({
          scale: [0.1, 0.1],
          src: "/arrow.svg",
          rotation,
          rotateWithView: true,
        }),
      }),
      ...circles.flatMap(({radius, legend}) => [
        new Style({
          geometry: new Point(point),
          image: new Circle({
            radius: radius,
            stroke: new Stroke({
              color: 'black',
              width: 1,
            }),
          }),
          text: new Text({
            offsetY: (radius - 10) * (legendRotation === 0 ? 1 : -1),
            rotation: legendRotation,
            text: legend,
            font: '14px Arial',
            fill: new Fill({color: '#fff'}),
            stroke: new Stroke({color: '#000', width: 2})
          })
        }),
        new Style({
          geometry: new Point(point),
          text: new Text({
            offsetY: - radius + 10,
            rotation: -legendRotation,
            text: legend,
            font: '14px Arial',
            fill: new Fill({color: '#fff'}),
            stroke: new Stroke({color: '#000', width: 2})
          })
        }),
      ])
    ]
  }
}

export default new PositionService() as IPositionService
